// components/chat/TranslatableMessage.js
// Renders a chat message and, for INCOMING messages, auto-translates it to the
// viewer's language on-device (Chrome built-in Translator). Your own messages
// render as-is. A small "See original / See translation" toggle appears only
// when a translation was actually produced. In unsupported browsers this is a
// transparent pass-through — it just shows the original text.
import { useState, useEffect } from 'react';
import { translateText, translateSupported } from '../../lib/chatTranslate';

export default function TranslatableMessage({ text, targetLang, isOwn, className }) {
  const [translated, setTranslated] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    let attempt = 0;
    setTranslated(null);
    setShowOriginal(false);
    if (isOwn || !text || !translateSupported()) return;

    const run = () => {
      attempt += 1;
      translateText(text, targetLang)
        .then((res) => {
          if (cancelled) return;
          if (res && res.text) {
            setTranslated(res.text);
            return;
          }
          // Chrome may still be downloading/preparing this language pair.
          // Retry twice without creating an unbounded translation loop.
          if (attempt < 3) retryTimer = setTimeout(run, attempt * 2000);
        })
        .catch(() => {
          if (!cancelled && attempt < 3) retryTimer = setTimeout(run, attempt * 2000);
        });
    };

    run();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [text, targetLang, isOwn]);

  const showTranslated = translated && !showOriginal;

  return (
    <>
      <p className={className}>{showTranslated ? translated : text}</p>
      {translated && (
        <button
          type="button"
          onClick={() => setShowOriginal((v) => !v)}
          className="mt-1 text-[11px] leading-none opacity-60 hover:opacity-100 underline focus:outline-none"
        >
          {showOriginal ? 'See translation' : 'See original'}
        </button>
      )}
    </>
  );
}
